import React, { useMemo, useState } from 'react';

import { Alert, Box, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@apigrowfinance/pbolt-button';
import TextDisplay from '@apigrowfinance/pbolt-text-display';
import TextDisplayBlock from '@apigrowfinance/pbolt-text-display-block';
import { useTheme } from '@apigrowfinance/pbolt-theme-provider';
import { FormattedMessage, useIntl } from 'react-intl';

import { AddressDetails } from '@clients/accountsClient';
import { DisputeResponse } from '@clients/disputesClient';
import { TransactionItem } from '@clients/transactionsClient';
import MapWrapperContainer from '@components/map/MapWrapperContainer';
import ReportDisputeDialog from '@components/transactions/reportDisputeDialog/ReportDisputeDialog';
import { getGoogleMapsAPIKey } from '@utils/URLs';

interface TransactionDetailsProps {
    transaction: TransactionItem;
    isLoading?: boolean;
    dispute: DisputeResponse;
    disputeLoading: boolean;
}

const TransactionDetails = ({
    transaction,
    isLoading,
    dispute,
    disputeLoading,
}: TransactionDetailsProps) => {
    const { theme } = useTheme();
    const { formatMessage } = useIntl();
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const hasMap = getGoogleMapsAPIKey() != '' && transaction?.merchant != null;
    const textBlocks = useMemo(() => {
        return [
            {
                label: formatMessage({
                    id: 'TransactionDetails.userCategory',
                    defaultMessage: 'Customer Category',
                }),
                value: transaction?.user_category,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.accountId',
                    defaultMessage: 'Account ID',
                }),
                value: transaction?.account_id,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.statementId',
                    defaultMessage: 'Statement ID',
                }),
                value: transaction?.statement_id,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.installment',
                    defaultMessage: 'Installment',
                }),
                value: transaction?.installment,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.numberOfInstallment',
                    defaultMessage: 'Number of Installments',
                }),
                value: transaction?.number_of_installments,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.processingCode',
                    defaultMessage: 'Processing Code',
                }),
                value: transaction?.processing_code,
            },
            {
                label: formatMessage({
                    id: 'TransactionDetails.processingDescription',
                    defaultMessage: 'Processing Description',
                }),
                value: transaction?.processing_description,
            },
        ];
    }, [formatMessage, transaction]);
    return (
        <Grid
            container
            item
            flexDirection="column"
            xs={12}
            sx={{
                bgcolor: theme?.palette?.background?.paper,
                pt: 4.375,
                pl: 2.625,
                py: 4,
                px: 3,
                mb: 0.25,
            }}>
            <TextDisplay
                label={
                    <FormattedMessage
                        id="TransactionDetails.softDesc"
                        defaultMessage="Description"
                    />
                }
                value={transaction?.soft_descriptor}
                isLoading={isLoading}
            />
            <Grid container item>
                <Grid container item xs={hasMap ? 9 : 12}>
                    <Grid xs={12} item display="flex" flexDirection="column">
                        <TextDisplayBlock
                            isLoading={isLoading}
                            textBlocks={textBlocks}
                        />
                        {transaction?.authorization?.type === 'NETWORK' &&
                            (!dispute?.id ? (
                                <Grid item display="flex" sx={{ mt: 'auto' }}>
                                    <LoadingButton
                                        data-testid="report-dispute"
                                        loading={disputeLoading}
                                        sx={{ mt: 3, ml: 0 }}
                                        variant="contained"
                                        onClick={() => {
                                            setReportDialogOpen(true);
                                        }}>
                                        <Typography>
                                            <FormattedMessage
                                                id="TransactionDetails.reportProblem"
                                                defaultMessage="Report problem"
                                            />
                                        </Typography>
                                    </LoadingButton>
                                </Grid>
                            ) : (
                                <Alert
                                    sx={{
                                        mt: 2,
                                    }}
                                    severity="info"
                                    data-testid="authorisation-disputed">
                                    <FormattedMessage
                                        id="TransactionDetails.Disputed"
                                        defaultMessage="This transaction has been disputed"
                                    />
                                </Alert>
                            ))}
                    </Grid>
                </Grid>
                {hasMap && (
                    <Grid item xs={3}>
                        <Box height={theme.spacing(53.875)}>
                            <MapWrapperContainer
                                idToCenterOn={transaction?.id.toString()}
                                disableControls
                                zoomLevel={10}
                                sxProps={{
                                    borderRadius: theme.shape.borderRadius,
                                    borderStyle: 'solid',
                                    borderWidth: 1,
                                    borderColor: theme.palette.grey[200],
                                }}
                                markerAddresses={[
                                    {
                                        id: transaction?.id,
                                        city: transaction?.merchant?.city,
                                        country: transaction?.merchant?.state,
                                    } as AddressDetails,
                                ]}
                            />
                        </Box>
                    </Grid>
                )}
            </Grid>
            {!!transaction?.authorization?.type && (
                <ReportDisputeDialog
                    open={reportDialogOpen}
                    closeCallback={() => {
                        setReportDialogOpen(false);
                    }}
                    transaction={transaction}
                />
            )}
        </Grid>
    );
};

export default TransactionDetails;
